import { useFetch } from "~vue/api";
import type { InviteAnonymized, InviteEnvelopeResponse } from "~vue/api/generated/reflections";
import AmplitudeEventNames from "~vue/utils/amplitudeEventNames";
import { logUserInteraction } from "~vue/utils/logUtils";
import { ref } from "vue";

const RESEND_COOLDOWN_MS = 24 * 60 * 60 * 1000;
const RESENT_BADGE_DURATION_MS = 3000;

export interface UseAssessmentInviteResendOptions {
    cycleId: string;
    setError: (message: string | null) => void;
    onInviteUpdated: (inviteId: string, invite: InviteAnonymized) => void;
}

export function formatInviteNotificationDate(isoStr: string | null | undefined): string {
    if (!isoStr) {
        return "";
    }
    const d = new Date(isoStr);
    if (Number.isNaN(d.getTime())) {
        return "";
    }
    return d.toLocaleDateString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
}

export function isInviteResendDisabled(invite: InviteAnonymized): boolean {
    if (!invite.last_reminder_at) {
        return false;
    }
    const lastSent = Date.parse(invite.last_reminder_at);
    return Number.isFinite(lastSent) && Date.now() - lastSent < RESEND_COOLDOWN_MS;
}

export function useAssessmentInviteResend({ cycleId, setError, onInviteUpdated }: UseAssessmentInviteResendOptions) {
    const resendingId = ref<string | null>(null);
    const resentIds = ref<Set<string>>(new Set());

    async function resendInvite(invite: InviteAnonymized): Promise<void> {
        if (resendingId.value || isInviteResendDisabled(invite)) {
            return;
        }
        resendingId.value = invite.id;
        setError(null);
        const { data, error: fetchError } = await useFetch(`/v1/reflections/cycles/${cycleId}/invites/${invite.id}/resend`).post().json<InviteEnvelopeResponse>();
        resendingId.value = null;

        if (fetchError.value) {
            setError(fetchError.value.message || "Could not resend invite.");
            return;
        }

        const updatedInvite = data.value?.invite;
        if (updatedInvite) {
            onInviteUpdated(invite.id, updatedInvite);
        }

        resentIds.value.add(invite.id);
        globalThis.setTimeout(() => resentIds.value.delete(invite.id), RESENT_BADGE_DURATION_MS);

        void logUserInteraction(AmplitudeEventNames.ASSESSMENT_RATER_INVITE_RESENT, {
            cycle_id: cycleId,
            invite_id: invite.id,
        });
    }

    return {
        resendingId,
        resentIds,
        resendInvite,
        isResendDisabled: isInviteResendDisabled,
        formatInviteNotificationDate,
    };
}
